import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROOM } from "@/constants/testIds";
import { FolderLock, Plus, X, Archive, MoreHorizontal, Pencil, Trash2, ArchiveRestore, ListTodo } from "lucide-react";
import ConfirmDeleteRoom from "@/components/ConfirmDeleteRoom";
import { toast } from "@/components/Toaster";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", brand_color: "#00684A", watermark_text: "" });
  const [err, setErr] = useState("");
  // Open-task count per room. Empty unless the user has Tasks access —
  // we just skip the call otherwise so non-tasks users don't see badges
  // (or hit pointless 403s).
  const [taskCounts, setTaskCounts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rooms");
      setRooms(data);
    } finally { setLoading(false); }
  };

  // Hydrate the per-room open-task count once we know the user has Tasks
  // access. Single round-trip aggregation on the backend so this scales
  // to dozens of rooms without N+1 fetches. Refreshes on every load() call
  // by piggy-backing the same effect dep.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: a } = await api.get("/tasks/access/me");
        if (!alive) return;
        if (!a?.has_access) { setTaskCounts({}); return; }
        const { data } = await api.get("/tasks/counts/by-room");
        if (alive) setTaskCounts(data || {});
      } catch {
        if (alive) setTaskCounts({});
      }
    })();
    return () => { alive = false; };
  }, [rooms]);

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/rooms", form);
      setShowCreate(false);
      setForm({ name: "", description: "", brand_color: "#00684A", watermark_text: "" });
      navigate(`/app/rooms/${data.id}`);
    } catch (e) { setErr(formatError(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-mono text-muted-foreground">— Workspace / Data Rooms</div>
          <h1 className="mt-3 font-display text-4xl font-medium tracking-tighter">Your sovereign vaults</h1>
          <p className="mt-2 text-sm text-muted-foreground">Manage M&amp;A deals, fundraising, and due-diligence projects.</p>
        </div>
        {user?.role === "admin" && (
          <button
            data-testid={ROOM.createBtn}
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition"
          >
            <Plus className="w-4 h-4" /> New data room
          </button>
        )}
      </div>

      {loading ? (
        <div className="label-mono text-muted-foreground">— Loading rooms —</div>
      ) : rooms.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <FolderLock className="w-10 h-10 mx-auto text-muted-foreground" />
          <div className="mt-4 font-display text-xl">No data rooms yet</div>
          <p className="text-sm text-muted-foreground mt-1">
            {user?.role === "admin" ? "Create your first room to get started." : "Wait for an administrator to invite you."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {rooms.map((r) => (
            <RoomCard key={r.id} room={r} isAdmin={user?.role === "admin"} openTasks={taskCounts[r.id] || 0} onChanged={load} />
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-card border border-border rounded-md w-full max-w-md p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl tracking-tight">New data room</h2>
              <button onClick={() => setShowCreate(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="label-mono">Name</label>
                <input
                  data-testid={ROOM.nameInput}
                  required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div>
                <label className="label-mono">Description</label>
                <textarea
                  data-testid={ROOM.descInput}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono">Brand color</label>
                  <input
                    type="color" value={form.brand_color}
                    onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                    className="mt-1 w-full h-10 bg-background border border-border rounded-md"
                  />
                </div>
                <div>
                  <label className="label-mono">Watermark text</label>
                  <input
                    data-testid={ROOM.watermarkInput}
                    value={form.watermark_text}
                    onChange={(e) => setForm({ ...form, watermark_text: e.target.value })}
                    placeholder="CONFIDENTIAL"
                    className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
              {err && <div className="text-sm text-destructive">{err}</div>}
              <button
                data-testid={ROOM.createSubmit}
                type="submit"
                className="w-full px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition"
              >
                Create data room
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


/* ------------------------------ RoomCard --------------------------------- */

function RoomCard({ room, isAdmin, openTasks = 0, onChanged }) {
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(room.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const saveRename = async () => {
    if (!name.trim() || name.trim() === room.name) { setRenaming(false); setName(room.name); return; }
    try {
      await api.patch(`/rooms/${room.id}`, { name: name.trim() });
      setRenaming(false);
      onChanged?.();
      toast.success("Room renamed");
    } catch (e) {
      toast.error(formatError(e));
      setName(room.name);
      setRenaming(false);
    }
  };

  const toggleArchive = async () => {
    try {
      await api.patch(`/rooms/${room.id}`, { archived: !room.archived });
      onChanged?.();
      toast.success(room.archived ? "Room restored" : "Room archived");
    } catch (e) { toast.error(formatError(e)); }
    setOpen(false);
  };

  return (
    <div className="bg-card p-6 relative group">
      <div className="flex items-start justify-between gap-2">
        <Link
          to={`/app/rooms/${room.id}`}
          data-testid={ROOM.openCard}
          className="flex items-start gap-3 flex-1 min-w-0"
        >
          <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
               style={{ background: room.brand_color }}>
            <FolderLock className="w-5 h-5 text-white" />
          </div>
        </Link>
        <div className="flex items-center gap-2">
          {room.archived && (
            <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground mono text-[10px] uppercase">
              Archived
            </span>
          )}
          {isAdmin && (
            <div className="relative" ref={menuRef}>
              <button
                data-testid="room-card-menu"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
                className="p-1.5 rounded-md hover:bg-secondary transition"
                aria-label="Room actions"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
              {open && (
                <div className="absolute right-0 top-8 w-44 bg-popover border border-border rounded-md shadow-xl z-30 py-1">
                  <button
                    data-testid="room-menu-rename"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRenaming(true); setOpen(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-2"
                  ><Pencil className="w-3.5 h-3.5" /> Rename</button>
                  <button
                    data-testid="room-menu-archive"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleArchive(); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-2"
                  >
                    {room.archived
                      ? <><ArchiveRestore className="w-3.5 h-3.5" /> Unarchive</>
                      : <><Archive className="w-3.5 h-3.5" /> Archive</>}
                  </button>
                  <div className="my-1 border-t border-border" />
                  <button
                    data-testid="room-menu-delete"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(true); setOpen(false); }}
                    className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2"
                  ><Trash2 className="w-3.5 h-3.5" /> Delete…</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {renaming ? (
        <div className="mt-4 flex gap-2">
          <input
            data-testid="room-rename-input"
            autoFocus value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") { setRenaming(false); setName(room.name); } }}
            className="flex-1 px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <button onClick={saveRename}
            data-testid="room-rename-save"
            className="px-3 py-1 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition">Save</button>
          <button onClick={() => { setRenaming(false); setName(room.name); }}
            className="px-3 py-1 text-xs rounded-md border border-border hover:bg-secondary transition">Cancel</button>
        </div>
      ) : (
        <Link to={`/app/rooms/${room.id}`} className="block">
          <h3 className="mt-4 font-display text-xl tracking-tight">{room.name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{room.description || "—"}</p>
        </Link>
      )}

      <div className="mt-5 flex items-center justify-between border-t border-border pt-3">
        <div className="flex items-center gap-2 flex-wrap">
          {room.deal_type && room.deal_type !== "Other" && (
            <span className="px-2 py-0.5 rounded-full bg-secondary mono text-[10px] uppercase tracking-wider">{room.deal_type}</span>
          )}
          {room.require_mfa && (
            <span className="px-2 py-0.5 rounded-full bg-accent/15 text-accent mono text-[10px] uppercase tracking-wider">MFA</span>
          )}
          {room.allow_downloads === false && (
            <span className="px-2 py-0.5 rounded-full bg-destructive/15 text-destructive mono text-[10px] uppercase tracking-wider">No DL</span>
          )}
          {openTasks > 0 && (
            <Link
              to={`/app/tasks?room=${room.id}`}
              onClick={(e) => e.stopPropagation()}
              data-testid={`room-card-task-badge-${room.id}`}
              title={`${openTasks} open task${openTasks === 1 ? "" : "s"} in this room`}
              className="px-2 py-0.5 rounded-full bg-primary/15 text-primary mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1 hover:bg-primary/25 transition"
            >
              <ListTodo className="w-3 h-3" />
              {openTasks} OPEN
            </Link>
          )}
        </div>
        <Link to={`/app/rooms/${room.id}`} className="label-mono text-primary group-hover:underline">Open →</Link>
      </div>

      {confirmDelete && (
        <ConfirmDeleteRoom
          room={room}
          onClose={() => setConfirmDelete(false)}
          onDeleted={() => { setConfirmDelete(false); onChanged?.(); }}
        />
      )}
    </div>
  );
}
